import {useEffect, useState} from "react";
import { ProcessStep } from "./ProcessStep";

export function CertificationProcess() {
  const [activeStep, setActiveStep] = useState(0);
  const [scrolling, setScrolling] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 6);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      if (!scrolling) {
        setScrolling(true);
        setTimeout(() => setScrolling(false), 800);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [scrolling]);

  return (
    <div className="max-w-6xl mx-auto px-2 sm:px-4 md:px-6">
      {/* Certification Levels - Mobile-first progression indicator */}
      <div className="mb-8 sm:mb-10 md:mb-12 lg:mb-16">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-lg sm:text-xl font-semibold text-gray-800 text-center mb-6">
            Certification Levels
          </h3>

          {/* Mobile: Vertical Stack, Desktop: Horizontal */}
          <div className="flex flex-col sm:flex-row justify-center items-center sm:items-start gap-6 sm:gap-8 lg:gap-12">
            {/* Bronze Level */}
            <div className="flex flex-col items-center max-w-xs text-center">
              <div className="relative">
                <div className="w-20 h-20 bg-amber-100 border-4 border-amber-700 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <span className="text-2xl">🥉</span>
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-amber-700 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  1
                </div>
              </div>
              <h4 className="text-lg font-bold text-amber-700 mb-2">BRONZE</h4>
            </div>

            {/* Connection line - hidden on mobile */}
            <div className="hidden sm:flex items-center justify-center flex-shrink-0 mt-10">
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <svg className="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="w-8 h-0.5 bg-gray-300"></div>
            </div>

            {/* Silver Level */}
            <div className="flex flex-col items-center max-w-xs text-center">
              <div className="relative">
                <div className="w-20 h-20 bg-gray-100 border-4 border-gray-400 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <span className="text-2xl">🥈</span>
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gray-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  2
                </div>
              </div>
              <h4 className="text-lg font-bold text-gray-600 mb-2">SILVER</h4>
            </div>

            {/* Connection line - hidden on mobile */}
            <div className="hidden sm:flex items-center justify-center flex-shrink-0 mt-10">
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <svg className="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="w-8 h-0.5 bg-gray-300"></div>
            </div>

            {/* Gold Level */}
            <div className="flex flex-col items-center max-w-xs text-center">
              <div className="relative">
                <div className="w-20 h-20 bg-yellow-100 border-4 border-yellow-500 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <span className="text-2xl">🥇</span>
                </div>
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-yellow-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  3
                </div>
              </div>
              <h4 className="text-lg font-bold text-yellow-600 mb-2">GOLD</h4>
            </div>
          </div>
        </div>
      </div>
      {/* Process Flow - Optimized for all screen sizes */}
      <div className="sm:px-2 md:px-4 lg:px-6">
        <div className="lg:flex lg:flex-row lg:flex-wrap lg:justify-center lg:items-start lg:gap-6">
          {/* For mobile to medium tablet: Vertical timeline */}
          {/* For large tablet and desktop: Horizontal layout */}
          <ProcessStep
            icon={<Calculator />}
            title="Fill Out Baseline Assessment"
            showArrow
            isActive={activeStep === 0}
            index={0}
          />
          <ProcessStep
            icon={<Checklist />}
            title="Complete Actions Checklist"
            showArrow
            isActive={activeStep === 1}
            index={1}
          />
          <ProcessStep
            icon={<Calculator />}
            title="Fill Out Savings Assessment"
            showArrow
            isActive={activeStep === 2}
            index={2}
          />
          <ProcessStep
            icon={<Document />}
            title="Submit for Audit"
            showArrow
            isActive={activeStep === 3}
            index={3}
          />
          <ProcessStep
            icon={<Shield />}
            title="Complete Audit"
            showArrow
            isActive={activeStep === 4}
            index={4}
          />
          <ProcessStep
            icon={<Certificate />}
            title="Successfully Certified for a Year"
            showArrow={false}
            isActive={activeStep === 5}
            index={5}
          />
        </div>
      </div>
    </div>
  );
}
// Icons
function Calculator() {
  return (
    <div className="w-24 h-24 border-4 border-green-600 flex flex-col justify-center items-center p-2">
      <div className="flex justify-center w-full">
        <div className="text-2xl text-green-600 font-bold">+</div>
      </div>
      <div className="flex justify-center w-full">
        <div className="text-2xl text-green-600 font-bold">×</div>
      </div>
    </div>
  );
}
function Checklist() {
  return (
    <div className="w-24 h-24 border-4 border-green-600 flex flex-col relative">
      <div className="absolute right-0 top-0 h-12 w-6 border-l-4 border-b-4 border-green-600"></div>
      <div className="pl-4 pt-6">
        <div className="flex items-center mb-1">
          <div className="w-4 h-4 text-green-600">✓</div>
          <div className="w-10 h-1 bg-green-600 ml-1"></div>
        </div>
        <div className="flex items-center mb-1">
          <div className="w-4 h-4 text-green-600">✓</div>
          <div className="w-10 h-1 bg-green-600 ml-1"></div>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 text-green-600">✓</div>
          <div className="w-10 h-1 bg-green-600 ml-1"></div>
        </div>
      </div>
    </div>
  );
}
function Document() {
  return (
    <div className="w-24 h-24 border-4 border-green-600 flex flex-col relative">
      <div className="absolute right-0 top-0 h-6 w-6 border-l-4 border-b-4 border-green-600"></div>
      <div className="flex flex-col justify-center h-full p-3">
        <div className="w-full h-1 bg-green-600 mb-2"></div>
        <div className="w-full h-1 bg-green-600 mb-2"></div>
        <div className="w-full h-1 bg-green-600 mb-2"></div>
        <div className="flex justify-center items-center">
          <div className="w-8 h-8 rounded-full border-4 border-green-600 flex items-center justify-center">
            <span className="text-green-600 text-xs">✓</span>
          </div>
        </div>
      </div>
    </div>
  );
}
function Shield() {
  return (
    <div className="w-24 h-24 flex justify-center items-center">
      <div
        className="w-20 h-20 border-4 border-green-600 rounded-t-full flex items-center justify-center"
        style={{
          borderBottomLeftRadius: "5px",
          borderBottomRightRadius: "5px",
        }}
      >
        <span className="text-green-600 text-2xl">✓</span>
      </div>
    </div>
  );
}
function Certificate() {
  return (
    <div className="w-24 h-24 border-4 border-green-600 flex flex-col relative">
      <div className="flex flex-col justify-center h-full p-3">
        <div className="w-full h-1 bg-green-600 mb-2"></div>
        <div className="w-full h-1 bg-green-600 mb-2"></div>
        <div className="w-full h-1 bg-green-600"></div>
      </div>
      <div className="absolute -right-2 -bottom-4">
        <div className="w-10 h-12 flex flex-col items-center">
          <div className="w-8 h-8 rounded-full border-2 border-green-600 flex items-center justify-center bg-white">
            <span className="text-green-600 text-xs">★</span>
          </div>
          <div className="w-2 h-6 bg-green-600 -mt-1"></div>
          <div className="flex">
            <div className="w-4 h-4 bg-white border-t-2 border-l-2 border-green-600 transform -rotate-45"></div>
            <div className="w-4 h-4 bg-white border-t-2 border-r-2 border-green-600 transform rotate-45"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
